import { INTERNAL_ORG_SLUG, organizationList } from "@ir/contracts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { StudyForm } from "@/features/studies/StudyForm";
import { ApiError, apiFetch } from "@/lib/api";
import { canManageStudies } from "@/lib/roles";
import { authClient, useSession } from "@/lib/authClient";
import { optionalProp } from "@/lib/optionalProp";

export const Route = createFileRoute("/_auth/admin/estudios/nuevo")({
  async beforeLoad() {
    const { data } = await authClient.getSession();
    if (!canManageStudies(data?.user.role)) redirect({ to: "/insights", throw: true });
  },
  component: NewStudyPage,
});

function NewStudyPage() {
  const { t } = useTranslation();
  const session = useSession();
  const organizations = useQuery({
    queryKey: ["organizations"],
    queryFn: () => apiFetch("/api/organizations", { schema: organizationList }),
    // Sin reintentos: si la lista de organizaciones falla, quedarse
    // reintentando en silencio deja al administrador viendo "Cargando…"
    // varios segundos antes de un error que además nunca llega a mostrarse
    // (ver el `if (organizations.isError)` de abajo).
    retry: false,
  });

  if (organizations.isError) {
    const error = organizations.error;
    return (
      <p className="px-8 py-6 text-nota text-warn">
        {error instanceof ApiError ? error.message : t("common.error")}
      </p>
    );
  }

  // Por slug, no por `kind`: ver la nota en INTERNAL_ORG_SLUG.
  const interna = organizations.data?.items.find((o) => o.slug === INTERNAL_ORG_SLUG);
  // Se espera a la sesión: el autor se prellena con su nombre y el
  // formulario fija sus valores iniciales una sola vez, al montarse.
  if (!interna || session.isPending) return <p className="px-8 py-6 text-nota text-ink3">{t("common.loading")}</p>;

  return (
    <StudyForm
      study={null}
      initialAccess={{ organizationIds: [], userIds: [] }}
      internalOrganizationId={interna.id}
      {...optionalProp("defaultAuthor", session.data?.user.name)}
    />
  );
}
